import ActionDialog from "@dashboard/components/ActionDialog/ActionDialog";
import {
  type AtticAssetDirectoryFragment,
  type AtticErrorFragment,
  useAtticAssetDirectoryCreateMutation,
  useAtticAssetDirectoryDeleteMutation,
  useAtticAssetDirectoryListQuery,
  useAtticAssetDirectoryUpdateMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { getMutationState } from "@dashboard/misc";
import { Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";

import {
  AtticAssetDirectoryDialog,
  type AtticAssetDirectoryDialogMode,
} from "../components/AtticAssetDirectoryDialog/AtticAssetDirectoryDialog";
import AtticAssetDirectoryListPage from "../components/AtticAssetDirectoryListPage/AtticAssetDirectoryListPage";
import { getAtticAssetDeleteRefusalMessage, isAtticAssetGoneRefusal } from "../errors";
import { directoryDialogMessages, sectionMessages } from "../messages";
import { atticAssetDirectoryUrl } from "../urls";

type DialogState =
  | { type: "closed" }
  | { type: "create" }
  | { type: "rename"; directory: AtticAssetDirectoryFragment }
  | { type: "delete"; directory: AtticAssetDirectoryFragment };

export const AtticAssetDirectoryListView = () => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [dialog, setDialog] = useState<DialogState>({ type: "closed" });
  // The refusals of this dialog session only: reopening the dialog starts clean.
  const [dialogErrors, setDialogErrors] = useState<AtticErrorFragment[]>([]);
  const { data, loading, error, refetch } = useAtticAssetDirectoryListQuery({
    fetchPolicy: "cache-and-network",
  });
  const close = () => setDialog({ type: "closed" });

  // The dialog shows create and rename refusals inline, so the wrapper's
  // toasts are off for those two; a delete refusal is one toast in merchant
  // words, and one for a directory already gone closes the dialog and
  // refreshes, so its stale row disappears. The wrapper still handles a lost
  // session and read-only mode.
  const [createDirectory, createOpts] = useAtticAssetDirectoryCreateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const payload = result.atticAssetDirectoryCreate;

      setDialogErrors(payload?.errors ?? []);

      if (payload && !payload.errors.length && payload.directory) {
        notify({ status: "success", text: intl.formatMessage(sectionMessages.directorySaved) });
        close();
        navigate(atticAssetDirectoryUrl(payload.directory.id));
      }
    },
  });
  const [renameDirectory, renameOpts] = useAtticAssetDirectoryUpdateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      setDialogErrors(result.atticAssetDirectoryUpdate?.errors ?? []);

      if (!result.atticAssetDirectoryUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(sectionMessages.directorySaved) });
        close();
      }
    },
  });
  const [deleteDirectory, deleteOpts] = useAtticAssetDirectoryDeleteMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const [refusal] = result.atticAssetDirectoryDelete?.errors ?? [];

      if (refusal) {
        const gone = isAtticAssetGoneRefusal(refusal);

        notify({
          status: gone ? "info" : "error",
          text: getAtticAssetDeleteRefusalMessage(refusal, intl, "directory"),
        });

        if (gone) {
          close();
          refetch();
        }

        return;
      }

      notify({ status: "success", text: intl.formatMessage(sectionMessages.directoryDeleted) });
      close();
      refetch();
    },
  });

  const dialogMode: AtticAssetDirectoryDialogMode =
    dialog.type === "rename"
      ? { type: "rename", name: dialog.directory.name, slug: dialog.directory.slug }
      : { type: "create" };
  const directoryDialogOpen = dialog.type === "create" || dialog.type === "rename";

  return (
    <>
      <AtticAssetDirectoryListPage
        store={data?.atticAssetStore ?? undefined}
        directories={data ? data.atticAssetDirectories : loading ? undefined : []}
        loadFailed={!data && !!error}
        onAdd={() => {
          setDialogErrors([]);
          setDialog({ type: "create" });
        }}
        onRename={directory => {
          setDialogErrors([]);
          setDialog({ type: "rename", directory });
        }}
        onDelete={directory => setDialog({ type: "delete", directory })}
      />
      <AtticAssetDirectoryDialog
        open={directoryDialogOpen}
        mode={dialogMode}
        errors={dialogErrors}
        submitting={dialog.type === "rename" ? renameOpts.loading : createOpts.loading}
        confirmButtonState={
          // A call refused at the top level (offline, a permission) has no
          // payload errors, and is a failure all the same.
          dialog.type === "rename"
            ? getMutationState(
                renameOpts.called,
                renameOpts.loading,
                dialogErrors,
                [renameOpts.error].filter(Boolean),
              )
            : getMutationState(
                createOpts.called,
                createOpts.loading,
                dialogErrors,
                [createOpts.error].filter(Boolean),
              )
        }
        onClose={close}
        onSubmit={({ name, slug }) =>
          dialog.type === "rename"
            ? renameDirectory({ variables: { id: dialog.directory.id, name } })
            : createDirectory({ variables: { input: { name, ...(slug ? { slug } : {}) } } })
        }
      />
      <ActionDialog
        open={dialog.type === "delete"}
        variant="delete"
        title={intl.formatMessage(directoryDialogMessages.deleteTitle)}
        confirmButtonState={getMutationState(
          deleteOpts.called,
          deleteOpts.loading,
          deleteOpts.data?.atticAssetDirectoryDelete?.errors ?? [],
        )}
        onClose={close}
        onConfirm={() =>
          dialog.type === "delete" && deleteDirectory({ variables: { id: dialog.directory.id } })
        }
      >
        {dialog.type === "delete" && (
          <Text size={3}>
            {intl.formatMessage(directoryDialogMessages.deleteContent, {
              name: dialog.directory.name,
              slug: dialog.directory.slug,
            })}
          </Text>
        )}
      </ActionDialog>
    </>
  );
};

export default AtticAssetDirectoryListView;
